import { useEffect, useRef } from 'react';
/**
 * Hook simples de atalhos globais.
 *
 * - "Mod" = Cmd (Mac) ou Ctrl (Windows/Linux), automaticamente.
 * - Ignora quando o foco está em input/textarea/contentEditable — exceto se
 *   o atalho for "Escape" (permite fechar overlays mesmo digitando).
 * - Handler recebe o KeyboardEvent caso queira controlar `preventDefault`.
 *
 * Passe a lista estável (useMemo) para evitar re-registros.
 */
export function useKeyboardShortcuts(shortcuts) {
    const ref = useRef(shortcuts);
    ref.current = shortcuts;
    useEffect(() => {
        function onKey(e) {
            const target = e.target;
            const tag = target?.tagName?.toLowerCase();
            const isEditing = tag === 'input' ||
                tag === 'textarea' ||
                target?.isContentEditable === true;
            for (const s of ref.current) {
                if (s.enabled === false)
                    continue;
                if (!matchKey(e, s.key))
                    continue;
                if (isEditing && s.key.toLowerCase() !== 'escape')
                    continue;
                e.preventDefault();
                s.handler(e);
                break;
            }
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);
}
function matchKey(e, combo) {
    const parts = combo.toLowerCase().split('+').map((p) => p.trim());
    const key = parts.pop() ?? '';
    const mods = new Set(parts);
    const hasMod = e.metaKey || e.ctrlKey;
    const needMod = mods.has('mod') || mods.has('cmd') || mods.has('ctrl');
    if (needMod !== hasMod)
        return false;
    if (mods.has('shift') !== e.shiftKey)
        return false;
    if (mods.has('alt') !== e.altKey)
        return false;
    const eKey = (e.key || '').toLowerCase();
    return eKey === key;
}
